import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useCity } from '../../contexts/CityContext';
import { useChat } from '../../contexts/ChatContext';
import config from '../../config';
import s from './Conversations.module.scss';

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================
const CONFIG = {
  API_BASE_URL: config.customerApiBase,
  POLLING_INTERVAL: 60000, // 60s refresh to reduce load
  DEBOUNCE_DELAY: 400,
};

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

// Debounced value hook - delays updates until user stops typing
const useDebounce = (value, delay = CONFIG.DEBOUNCE_DELAY) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

// API fetch hook with abort controller for cleanup
const useFetchData = (url, pollingInterval = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(url, {
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Fetch error:', err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    fetchData();

    if (pollingInterval) {
      const interval = setInterval(fetchData, pollingInterval);
      return () => {
        clearInterval(interval);
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, pollingInterval]);

  return { data, loading, error, refetch: fetchData };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const Conversations = () => {
  const history = useHistory();
  const { selectedCity } = useCity();
  const { openChat } = useChat();
  const [searchTerm, setSearchTerm] = useState('');
  const [unflaggingId, setUnflaggingId] = useState(null);
  const [confirmUnflag, setConfirmUnflag] = useState(null);
  const [unflaggedCount, setUnflaggedCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const refreshIntervalSeconds = CONFIG.POLLING_INTERVAL / 1000;
  const [nextRefreshIn, setNextRefreshIn] = useState(refreshIntervalSeconds);
  const itemsPerPage = 20;

  const debouncedSearchTerm = useDebounce(searchTerm);

  const flaggedApiUrl = React.useMemo(() => {
    const baseUrl = `${CONFIG.API_BASE_URL}/customers/flagged`;
    if (selectedCity && selectedCity !== 'All') {
      return `${baseUrl}?city=${encodeURIComponent(selectedCity)}`;
    }
    return baseUrl;
  }, [selectedCity]);

  const {
    data: countData,
    loading: countLoading,
    refetch: refetchCount,
  } = useFetchData(`${CONFIG.API_BASE_URL}/customers/count`, CONFIG.POLLING_INTERVAL);

  const {
    data: customersData,
    loading: customersLoading,
    error: customersError,
    refetch: refetchCustomers,
  } = useFetchData(flaggedApiUrl, CONFIG.POLLING_INTERVAL);

  const customerCount = countData?.count || 0;
  const flaggedCustomers = customersData?.customers || [];

  // Reset page when search or city changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedCity]);

  // Reset refresh countdown when data updates
  useEffect(() => {
    if (customersData || countData) {
      setNextRefreshIn(refreshIntervalSeconds);
    }
  }, [customersData, countData, refreshIntervalSeconds]);

  // Tick down countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const filteredCustomers = React.useMemo(() => {
    if (!debouncedSearchTerm) return flaggedCustomers;

    const searchLower = debouncedSearchTerm.toLowerCase();
    return flaggedCustomers.filter((customer) => {
      const nameMatch = customer.name?.toLowerCase().includes(searchLower);
      const phoneMatch = customer.phone?.toString().includes(debouncedSearchTerm);
      return nameMatch || phoneMatch;
    });
  }, [flaggedCustomers, debouncedSearchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const currentCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setPageInput(currentPage);
  }, [currentPage]);

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(pageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= totalPages) {
        handlePageChange(num);
      } else {
        setPageInput(currentPage);
      }
    }
  };

  const handlePageInputBlur = () => {
    setPageInput(currentPage);
  };

  const handleUnflagRequest = useCallback((customerId) => {
    const customer = flaggedCustomers.find((c) => c.id === customerId);
    setConfirmUnflag({ id: customerId, name: customer?.name || 'this customer' });
  }, [flaggedCustomers]);

  const handleUnflagConfirm = useCallback(async () => {
    if (!confirmUnflag) return;

    try {
      setUnflaggingId(confirmUnflag.id);
      const response = await fetch(
        `${CONFIG.API_BASE_URL}/customers/${confirmUnflag.id}/unflag`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error(`Failed to unflag customer: ${response.statusText}`);
      }

      setUnflaggedCount(prev => prev + 1);

      const successToast = document.createElement('div');
      successToast.className = 'alert alert-success position-fixed';
      successToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
      successToast.innerHTML = `
        <i class="fa fa-check-circle mr-2"></i>
        Customer unflagged successfully
      `;
      document.body.appendChild(successToast);
      setTimeout(() => successToast.remove(), 3000);

      refetchCount();
      refetchCustomers();
    } catch (error) {
      console.error('Error unflagging customer:', error);
      
      const errorToast = document.createElement('div');
      errorToast.className = 'alert alert-danger position-fixed';
      errorToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
      errorToast.innerHTML = `
        <i class="fa fa-exclamation-circle mr-2"></i>
        Failed to unflag customer: ${error.message}
      `;
      document.body.appendChild(errorToast);
      setTimeout(() => errorToast.remove(), 5000);
    } finally {
      setUnflaggingId(null);
      setConfirmUnflag(null);
    }
  }, [confirmUnflag, refetchCount, refetchCustomers]);

  const handleNewBooking = useCallback(() => {
    history.push('/app/customers/new-booking');
  }, [history]);

  const handleGoToCustomers = useCallback(() => {
    history.push('/app/customers');
  }, [history]);

  const handleViewDetails = useCallback((customerId) => {
    openChat({ customerId });
  }, [openChat]);

  const handleRetry = useCallback(() => {
    refetchCount();
    refetchCustomers();
  }, [refetchCount, refetchCustomers]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isLoading = countLoading || customersLoading;
  const hasError = customersError;

  return (
    <div className={s.root}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerLeft}>
          <h1 className={s.title}>
            <div className={s.titleIcon}>
              <i className="fa fa-flag" />
            </div>
            Flagged Conversations
            {selectedCity && selectedCity !== 'All' && (
              <span className={s.cityBadge}>
                <i className="fa fa-map-marker" />
                {selectedCity}
              </span>
            )}
          </h1>
          <p className={s.subtitle}>
            {selectedCity && selectedCity !== 'All'
              ? `Showing flagged conversations from ${selectedCity}`
              : 'Monitor and manage customer conversations requiring attention'}
          </p>
        </div>
        <div className={s.headerRight}>
          <button className={s.btnCustomers} onClick={handleGoToCustomers}>
            <i className="fa fa-users" />
            Customers
          </button>
          <button className={s.btnNewBooking} onClick={handleNewBooking}>
            <i className="fa fa-plus" />
            New Booking
          </button>
          <button 
            className={s.refreshBtn} 
            onClick={handleRetry}
            disabled={isLoading}
            title={`Auto-refreshing in ${Math.ceil(nextRefreshIn)}s`}
          >
            <i className={`fa ${isLoading ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
            {!isLoading && (
              <div className={s.refreshProgressTrack}>
                <div 
                  className={s.refreshProgressBar}
                  style={{ width: `${Math.max(0, Math.min(100, (nextRefreshIn / refreshIntervalSeconds) * 100))}%` }}
                />
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className={s.statsRow}>
        <div className={`${s.statCard} ${s.danger}`}>
          <div className={s.statIcon}>
            <i className="fa fa-flag" />
          </div>
          <div className={s.statContent}>
            <div className={s.statValue}>{customerCount}</div>
            <div className={s.statLabel}>Total Flagged</div>
          </div>
        </div>
        <div className={`${s.statCard} ${s.info}`}>
          <div className={s.statIcon}>
            <i className="fa fa-filter" />
          </div>
          <div className={s.statContent}>
            <div className={s.statValue}>{filteredCustomers.length}</div>
            <div className={s.statLabel}>Filtered Results</div>
          </div>
        </div>
        <div className={`${s.statCard} ${s.success}`}>
          <div className={s.statIcon}>
            <i className="fa fa-check-circle" />
          </div>
          <div className={s.statContent}>
            <div className={s.statValue}>{unflaggedCount}</div>
            <div className={s.statLabel}>Unflagged Today</div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className={s.searchSection}>
        <div className={s.searchWrapper}>
          <div className={s.searchInputGroup}>
            <span className={s.searchIcon}>
              <i className="fa fa-search" />
            </span>
            <input
              type="text"
              className={s.searchInput}
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className={s.clearBtn} onClick={() => setSearchTerm('')}>
                <i className="fa fa-times" />
              </button>
            )}
          </div>
          {searchTerm && (
            <span className={s.resultBadge}>
              {filteredCustomers.length} result{filteredCustomers.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Table Card */}
      <div className={s.tableCard}>
        {hasError ? (
          <div className={s.errorState}>
            <div className={s.errorIcon}>
              <i className="fa fa-exclamation-circle" />
            </div>
            <p>Error loading flagged conversations: {customersError}</p>
            <button className={s.retryBtn} onClick={handleRetry}>
              <i className="fa fa-refresh" /> Retry
            </button>
          </div>
        ) : isLoading && filteredCustomers.length === 0 ? (
          <div className={s.loadingState}>
            <div className={s.spinner} />
            <p>Loading flagged conversations...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>
              <i className="fa fa-check" />
            </div>
            <h3>No Flagged Conversations</h3>
            <p>
              {searchTerm
                ? `No customers match "${searchTerm}"`
                : 'All clear! No conversations require attention.'}
            </p>
          </div>
        ) : (
          <div className={s.tableWrapper}>
            <table className={s.dataTable}>
              <thead>
                <tr>
                  <th><i className="fa fa-hashtag" />ID</th>
                  <th><i className="fa fa-user" />Name</th>
                  <th><i className="fa fa-phone" />Phone</th>
                  <th><i className="fa fa-star" />Rating</th>
                  <th><i className="fa fa-exclamation-triangle" />Reason</th>
                  <th><i className="fa fa-calendar" />Flagged At</th>
                  <th><i className="fa fa-cog" />Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentCustomers.map((customer) => (
                  <tr key={customer.id}>
                    <td className={s.idCell}>{customer.id}</td>
                    <td className={s.nameCell}>
                      {customer.name || 'N/A'}
                      {customer.business_name && customer.business_name.trim() && customer.business_name.trim() !== (customer.name || '').trim() && (
                        <div className={s.businessName}>{customer.business_name}</div>
                      )}
                    </td>
                    <td>
                      <div className={s.phoneCell}>
                        <i className="fa fa-phone" />
                        {customer.phone || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <span className={`${s.ratingBadge} ${customer.rating ? s.hasRating : s.noRating}`}>
                        {customer.rating ? (
                          <>
                            <i className="fa fa-star" />
                            {customer.rating}
                          </>
                        ) : (
                          'No Rating'
                        )}
                      </span>
                    </td>
                    <td>
                      {customer.failure_reason ? (
                        <span className={s.reasonBadge} title={customer.failure_reason}>
                          <i className="fa fa-exclamation-triangle" />
                          <span>{customer.failure_reason}</span>
                        </span>
                      ) : (
                        <span className={`${s.reasonBadge} ${s.noReason}`}>N/A</span>
                      )}
                    </td>
                    <td className={s.timeCell}>
                      <i className="fa fa-clock-o" />
                      {formatDateTime(customer.created_at)}
                    </td>
                    <td>
                      <div className={s.actionsCell}>
                        <button
                          className={s.detailsBtn}
                          onClick={() => handleViewDetails(customer.id)}
                        >
                          <i className="fa fa-eye" />
                          Details
                        </button>
                        <button
                          className={s.unflagBtn}
                          onClick={() => handleUnflagRequest(customer.id)}
                          disabled={unflaggingId === customer.id}
                        >
                          <i className={`fa ${unflaggingId === customer.id ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                          Unflag
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination Controls */}
        {!isLoading && !hasError && filteredCustomers.length > itemsPerPage && (
          <div className={s.pagination}>
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className={s.pageBtn}
            >
              <i className="fa fa-chevron-left" /> Previous
            </button>
            <span className={s.pageInfo}>
              Page
              <input
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                onChange={handlePageInputChange}
                onKeyDown={handlePageInputKeyDown}
                onBlur={handlePageInputBlur}
                className={s.pageInput}
              />
              of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className={s.pageBtn}
            >
              Next <i className="fa fa-chevron-right" />
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmUnflag && (
        <div className={s.confirmModal}>
          <div className={s.confirmContent}>
            <div className={s.confirmHeader}>
              <h3>
                <i className="fa fa-question-circle" />
                Confirm Unflag
              </h3>
              <button className={s.closeBtn} onClick={() => setConfirmUnflag(null)}>
                ×
              </button>
            </div>
            <div className={s.confirmBody}>
              <p>
                Are you sure you want to unflag <strong>{confirmUnflag.name}</strong>?
              </p>
              <small>This customer will be removed from the flagged list.</small>
            </div>
            <div className={s.confirmFooter}>
              <button className={s.cancelBtn} onClick={() => setConfirmUnflag(null)}>
                <i className="fa fa-times" />
                Cancel
              </button>
              <button className={s.confirmBtn} onClick={handleUnflagConfirm}>
                <i className="fa fa-check" />
                Yes, Unflag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conversations;